/**
 * The BD's own phone and BU on a nomination — validated here as the service validates them
 * (AWSECEventHub BdProfileService / BdBusinessUnitService), so the BD sees a mistake before
 * submitting rather than as a 400 after.
 *
 * Phone: a mainland China mobile, 11 digits starting 13–19, optionally prefixed +86 / 86, with
 * spaces and hyphens ignored. The service stores the bare 11 digits; the form may submit what was
 * typed, since the service normalises it the same way.
 *
 * BU: not validated here, because it is not the BD's to supply. The service resolves it from the
 * GCR sales hierarchy, returns it on `GET /me` (`bdBusinessUnit`) and stamps it on the nomination
 * itself. A BD whose territories span several BUs (some leaders do) gets the first, for simplicity;
 * `bdBusinessUnitsOf` lists them all so the form can say so.
 *
 * Here rather than in the page because the coverage config measures only `.ts`.
 */

const MOBILE = /^(?:\+?86)?1[3-9]\d{9}$/;

/** What the phone pattern is checked against: the input without spaces or hyphens. */
export function compactPhone(raw: string): string {
  return raw.replace(/[\s-]/g, '');
}

export function isValidBdPhone(raw: string): boolean {
  return MOBILE.test(compactPhone(raw.trim()));
}

/** The model's `BusinessUnit` length cap; a longer node is skipped, as the service skips it. */
const MAX_BU_LENGTH = 64;

/**
 * Every BU the BD's territories fall under, in the order the service picks from — the first is
 * their `bdBusinessUnit`. Mirrors AWSECEventHub BdBusinessUnitService: the SH_L3 node names, trimmed
 * and de-duplicated, or the SH_L2 ones when no path reaches SH_L3. Display only (the "you span
 * several BUs" note); the service, not this, decides the BU.
 */
export function bdBusinessUnitsOf(
  l2: readonly string[] | undefined,
  l3: readonly string[] | undefined,
): readonly string[] {
  const names = (nodes: readonly string[] | undefined) => [
    ...new Set(
      (nodes ?? []).map((n) => n.trim()).filter((n) => n !== '' && n.length <= MAX_BU_LENGTH),
    ),
  ];
  const fromL3 = names(l3);
  return fromL3.length ? fromL3 : names(l2);
}

/**
 * Every BU name the BD sits under, for matching against an event's BUs (utils/nominatableEvents):
 * their resolved BU and both hierarchy levels, trimmed and de-duplicated. Wider than
 * {@link bdBusinessUnitsOf} on purpose — the event BU list mixes SH_L2 divisions (STRATEGIC, SMB,
 * HK, FSI-DNB) with SH_L3 groups (AUTO & MFG, ISV & SUP, …), so a BD under SMB must match an SMB
 * event whichever level names it. Mirrors AWSECEventHub BdBusinessUnitService `matchableNamesOf`.
 */
export function nominationBusinessUnitsOf(
  businessUnit: string | undefined,
  l2: readonly string[] | undefined,
  l3: readonly string[] | undefined,
): readonly string[] {
  return [
    ...new Set(
      [businessUnit ?? '', ...(l3 ?? []), ...(l2 ?? [])]
        .map((n) => n.trim())
        .filter((n) => n !== '' && n.length <= MAX_BU_LENGTH),
    ),
  ];
}
